<template>
    <div class="web-tab-viewer">
        <!-- WhatsApp Chat Starter Modal -->
        <Modal2
            v-if="showWhatsappChatStarterModal"
            @cancel="showWhatsappChatStarterModal = false"
            :title="$t('message.whatsappChatStart')"
            :showFooter="false"
        >
            <WhatsappChatStarter
                :default-phone-number="defaultPhoneNumber"
                phone-number-override
                @close="showWhatsappChatStarterModal = false"
            />
        </Modal2>

        <!-- Loading State -->
        <div v-if="loading" class="viewer-loading">
            <div class="loader-container">
                <div class="loader-spinner"></div>
                <div class="loader-text">Web sekmesi yükleniyor...</div>
            </div>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="viewer-error">
            <div class="error-container">
                <div class="error-icon-wrapper">
                    <span class="material-icons error-icon">error_outline</span>
                </div>
                <h2 class="error-title">Web Sekmesi Yüklenemedi</h2>
                <p class="error-message">{{ error }}</p>
                <div class="error-actions">
                    <button class="btn-retry" @click="loadWebTab">
                        <span class="material-icons">refresh</span>
                        Tekrar Dene
                    </button>
                    <button class="btn-back" @click="$router.push('/integrations/webtabs')">
                        <span class="material-icons">arrow_back</span>
                        Geri Dön
                    </button>
                </div>
            </div>
        </div>

        <!-- Content -->
        <div v-else-if="finalUrl" class="viewer-content" :class="{ embedded: isEmbedded }">
            <!-- Header (gizle embedded modda) -->
            <div class="viewer-header" v-if="!isEmbedded">
                <div class="header-left">
                    <button class="btn-icon" @click="$router.go(-1)" v-tippy content="Geri">
                        <span class="material-icons">arrow_back</span>
                    </button>
                    <div class="header-info">
                        <span 
                            class="header-icon material-icons" 
                            :style="{ color: webTab.iconColor || '#6366f1' }"
                        >
                            {{ webTab.icon }}
                        </span>
                        <div class="header-text">
                            <h1 class="header-title">{{ webTab.name }}</h1>
                            <span class="header-desc" v-if="webTab.description">{{ webTab.description }}</span>
                        </div>
                    </div>
                </div>
                <div class="header-right">
                    <button 
                        class="btn-icon" 
                        @click="openInNewTab" 
                        v-tippy 
                        content="Yeni sekmede aç"
                    >
                        <span class="material-icons">open_in_new</span>
                    </button>
                    <button 
                        class="btn-icon" 
                        @click="refreshIframe" 
                        v-tippy 
                        content="Yenile"
                    >
                        <span class="material-icons">refresh</span>
                    </button>
                    <button 
                        class="btn-icon fullscreen-btn" 
                        :class="{ active: isFullscreen }"
                        @click="toggleFullscreen" 
                        v-tippy 
                        :content="isFullscreen ? 'Tam ekrandan çık' : 'Tam ekran'"
                    >
                        <span class="material-icons">{{ isFullscreen ? 'fullscreen_exit' : 'fullscreen' }}</span>
                    </button>
                </div>
            </div>

            <!-- Embedded modda mini toolbar -->
            <div class="embedded-toolbar" v-if="isEmbedded">
                <button 
                    class="btn-icon-small" 
                    @click="openInNewTab" 
                    v-tippy 
                    content="Yeni sekmede aç"
                >
                    <span class="material-icons">open_in_new</span>
                </button>
                <button 
                    class="btn-icon-small" 
                    @click="refreshIframe" 
                    v-tippy 
                    content="Yenile"
                >
                    <span class="material-icons">refresh</span>
                </button>
            </div>

            <!-- Iframe Container -->
            <div class="iframe-container" :class="{ fullscreen: isFullscreen, embedded: isEmbedded }">
                <iframe 
                    ref="webTabIframe"
                    :src="finalUrl" 
                    class="web-tab-iframe"
                    frameborder="0"
                    allow="camera; microphone; geolocation; fullscreen; autoplay; payment; vr; web-share; accelerometer; gyroscope; magnetometer; picture-in-picture; display-capture; clipboard-read; clipboard-write"
                    sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-pointer-lock allow-orientation-lock allow-modals allow-downloads allow-popups-to-escape-sandbox allow-presentation allow-storage-access-by-user-activation allow-top-navigation allow-top-navigation-by-user-activation"
                    @load="handleIframeLoad"
                />
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import SYSTEM_PUBSUB from "../../../utils/systemPubSub";
import WhatsappChatStarter from "../Integrations/meta-leads/WhatsappChatStarter.vue";
import Modal2 from "../../../components/common/Modal2.vue";

const COMMANDS = {
    CLOSE_SUPSIS_EXTERNAL_WIDGET: "close-supsis-external-widget",
    OPEN_URL_IN_NEW_TAB: "open_url_in_new_tab",
    VIEW_RECORD: "view_record",
    START_CHAT: "start_chat",
    CREATE_TASK: "create_task",
    SHOW_WINDOW: "show_window",
    SHOW_CONTACT: "show_contact",
    SHOW_MESSAGE: "show_message",
    MAKE_CALL: "make_call",
};

export default {
    name: "WebTabViewer",
    components: {
        Modal2,
        WhatsappChatStarter,
    },
    props: {
        webTabProp: {
            type: Object,
            default: null,
        },
        contextType: {
            type: String,
            default: null, // "table-record", "task", null for route-based
        },
        context: {
            type: Object,
            default: null,
        },
        embedded: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            loading: true,
            error: null,
            webTab: null,
            finalUrl: null,
            isFullscreen: false,
            iframeKey: 0,
            showWhatsappChatStarterModal: false,
            defaultPhoneNumber: "",
        };
    },
    computed: {
        ...mapGetters({
            getWebTabUrl: "webtabs/getWebTabUrl",
        }),
        isEmbedded() {
            return this.embedded || this.webTabProp !== null;
        },
    },
    methods: {
        ...mapActions({
            getWebTab: "webtabs/getWebTab",
            addNewWindow: "app/addNewWindow",
        }),
        async loadWebTab() {
            this.loading = true;
            this.error = null;

            try {
                // Prop olarak geçilmişse kullan, değilse route'dan yükle
                if (this.webTabProp) {
                    this.webTab = this.webTabProp;
                } else {
                    const webTabId = this.$route.params.id;
                    if (!webTabId) {
                        throw new Error("Web sekmesi ID'si bulunamadı");
                    }
                    this.webTab = await this.getWebTab(webTabId);
                }

                if (!this.webTab.enabled) {
                    throw new Error("Bu web sekmesi şu anda aktif değil");
                }

                // URL'i store getter ile oluştur, sonra context parametrelerini çözümle
                let url = this.getWebTabUrl(this.webTab);
                
                if (!url) {
                    throw new Error("Web sekmesi URL'si oluşturulamadı");
                }

                // Context-aware parametre çözümleme
                if (this.contextType && this.context) {
                    url = this.resolveContextParams(url);
                }

                this.finalUrl = url;
            } catch (error) {
                console.error("Error loading web tab:", error);
                this.error = error.message || "Web sekmesi yüklenirken bir hata oluştu";
            } finally {
                this.loading = false;
            }
        },
        resolveContextParams(url) {
            if (this.contextType === "table-record") {
                return this.resolveTableRecordParams(url);
            }
            if (this.contextType === "task") {
                return this.resolveTaskParams(url);
            }
            return url;
        },
        resolveTableRecordParams(url) {
            var context = this.context || {};
            var table = context.table || {};
            var record = context.record || {};
            var tableId = context.tableId || "";
            var recordId = context.recordId || "";

            // Hem normal hem URL-encoded placeholder'ları replace et
            var result = url
                // Normal placeholders
                .replace(/\$\{table\.id\}/g, tableId)
                .replace(/\$\{table\.name\}/g, encodeURIComponent(table.name || ""))
                .replace(/\$\{record\.id\}/g, record._id || recordId)
                .replace(/\$\{record\.recordId\}/g, record.recordId || "")
                // URL-encoded placeholders (%24%7B = ${, %7D = })
                .replace(/%24%7Btable\.id%7D/gi, tableId)
                .replace(/%24%7Btable\.name%7D/gi, encodeURIComponent(table.name || ""))
                .replace(/%24%7Brecord\.id%7D/gi, record._id || recordId)
                .replace(/%24%7Brecord\.recordId%7D/gi, record.recordId || "");

            // Field değerlerini çözümle: ${field.FIELD_ID} ve encoded versiyonu
            result = result.replace(/\$\{field\.([^}]+)\}/g, function(match, fieldId) {
                var data = record.data || {};
                var value = data[fieldId];
                if (value === undefined || value === null) return "";
                if (typeof value === "object") return encodeURIComponent(JSON.stringify(value));
                return encodeURIComponent(String(value));
            });

            // URL-encoded field placeholders: %24%7Bfield.FIELD_ID%7D
            result = result.replace(/%24%7Bfield\.([^%]+)%7D/gi, function(match, fieldId) {
                var data = record.data || {};
                var value = data[fieldId];
                if (value === undefined || value === null) return "";
                if (typeof value === "object") return encodeURIComponent(JSON.stringify(value));
                return encodeURIComponent(String(value));
            });

            return result;
        },
        resolveTaskParams(url) {
            var task = this.context || {};
            var owner = task.owner || {};
            var usersMap = this.$store.state.user.users.data.reduce(function(acc, user) {
                acc[user._id] = user;
                return acc;
            }, {});

            var ownerUser = typeof owner === "string" ? usersMap[owner] : owner;
            var assigneeNames = (task.assignees || []).map(function(id) {
                var user = usersMap[id];
                return user ? user.name : id;
            }).join(", ");

            var taskId = task._id || "";
            var taskTitle = encodeURIComponent(task.title || "");
            var taskDescription = encodeURIComponent((task.description || "").replace(/<[^>]*>/g, ""));
            var taskPriority = task.priority || "";
            var taskStartDate = task.startDate || "";
            var taskEndDate = task.endDate || "";
            var taskCompleted = String(task.completed || false);
            var taskDone = String(task.done || false);
            var taskPipelineStage = task.pipelineStage || "";
            var taskWorkflow = task.workflow || "";
            var ownerId = typeof owner === "string" ? owner : (owner._id || "");
            var ownerName = encodeURIComponent(ownerUser ? ownerUser.name : "");
            var ownerEmail = encodeURIComponent(ownerUser ? ownerUser.email : "");
            var assigneesStr = (task.assignees || []).join(",");
            var assigneeNamesStr = encodeURIComponent(assigneeNames);

            var result = url
                // Normal placeholders
                .replace(/\$\{task\.id\}/g, taskId)
                .replace(/\$\{task\.title\}/g, taskTitle)
                .replace(/\$\{task\.description\}/g, taskDescription)
                .replace(/\$\{task\.priority\}/g, taskPriority)
                .replace(/\$\{task\.startDate\}/g, taskStartDate)
                .replace(/\$\{task\.endDate\}/g, taskEndDate)
                .replace(/\$\{task\.completed\}/g, taskCompleted)
                .replace(/\$\{task\.done\}/g, taskDone)
                .replace(/\$\{task\.pipelineStage\}/g, taskPipelineStage)
                .replace(/\$\{task\.workflow\}/g, taskWorkflow)
                .replace(/\$\{task\.owner\.id\}/g, ownerId)
                .replace(/\$\{task\.owner\.name\}/g, ownerName)
                .replace(/\$\{task\.owner\.email\}/g, ownerEmail)
                .replace(/\$\{task\.assignees\}/g, assigneesStr)
                .replace(/\$\{task\.assigneesNames\}/g, assigneeNamesStr)
                // URL-encoded placeholders
                .replace(/%24%7Btask\.id%7D/gi, taskId)
                .replace(/%24%7Btask\.title%7D/gi, taskTitle)
                .replace(/%24%7Btask\.description%7D/gi, taskDescription)
                .replace(/%24%7Btask\.priority%7D/gi, taskPriority)
                .replace(/%24%7Btask\.startDate%7D/gi, taskStartDate)
                .replace(/%24%7Btask\.endDate%7D/gi, taskEndDate)
                .replace(/%24%7Btask\.completed%7D/gi, taskCompleted)
                .replace(/%24%7Btask\.done%7D/gi, taskDone)
                .replace(/%24%7Btask\.pipelineStage%7D/gi, taskPipelineStage)
                .replace(/%24%7Btask\.workflow%7D/gi, taskWorkflow)
                .replace(/%24%7Btask\.owner\.id%7D/gi, ownerId)
                .replace(/%24%7Btask\.owner\.name%7D/gi, ownerName)
                .replace(/%24%7Btask\.owner\.email%7D/gi, ownerEmail)
                .replace(/%24%7Btask\.assignees%7D/gi, assigneesStr)
                .replace(/%24%7Btask\.assigneesNames%7D/gi, assigneeNamesStr);

            return result;
        },
        handleIframeLoad() {
            console.log("Iframe loaded successfully");
        },
        openInNewTab() {
            if (this.finalUrl) {
                window.open(this.finalUrl, "_blank");
            }
        },
        refreshIframe() {
            if (this.$refs.webTabIframe) {
                this.$refs.webTabIframe.src = this.finalUrl;
            }
        },
        toggleFullscreen() {
            this.isFullscreen = !this.isFullscreen;
        },
        handleKeydown(e) {
            if (e.key === "Escape" && this.isFullscreen) {
                this.isFullscreen = false;
            }
        },
        handleWidgetMessage(event) {
            if (event.data && event.data.type === COMMANDS.CLOSE_SUPSIS_EXTERNAL_WIDGET) {
                this.$router.go(-1);
            } else if (event.data && event.data.type === COMMANDS.VIEW_RECORD) {
                const { title, tableId, recordId, multiple, autoClose } = event.data;
                this.addNewWindow({
                    id: recordId,
                    type: "table-record-viewer",
                    title,
                    payload: {
                        tableId,
                        recordId,
                    },
                    multiple,
                    autoClose,
                });
            } else if (event.data && event.data.type === COMMANDS.START_CHAT) {
                const { phoneNumber, close } = event.data;
                this.defaultPhoneNumber = phoneNumber;
                this.showWhatsappChatStarterModal = true;
                if (close) this.$router.go(-1);
            } else if (event.data && event.data.type === COMMANDS.CREATE_TASK) {
                this.addNewWindow({
                    type: "chat-create-task",
                    title: this.$t("task.createTaskMessage"),
                });
            } else if (event.data && event.data.type === COMMANDS.SHOW_WINDOW) {
                const { type, title, payload, footer } = event.data;
                this.addNewWindow({
                    type,
                    title,
                    payload,
                    footer,
                });
            } else if (event.data && event.data.type === COMMANDS.SHOW_CONTACT) {
                const { title, contactId } = event.data;
                this.addNewWindow({
                    type: "show-visitor",
                    title,
                    payload: {
                        visitorId: contactId,
                    },
                    footer: false,
                });
            } else if (event.data && event.data.type === COMMANDS.SHOW_MESSAGE) {
                const { notificationType, message, description } = event.data;
                this.$notification[notificationType]({
                    message,
                    description,
                });
            } else if (event.data && event.data.type === COMMANDS.MAKE_CALL) {
                const { phoneNumber, close } = event.data;
                SYSTEM_PUBSUB.publish(SYSTEM_PUBSUB.MESSAGE_TYPES.WEBPHONE_CALL_START, {
                    phoneNumber,
                });
                if (close) this.$router.go(-1);
            } else if (event.data && event.data.type === COMMANDS.OPEN_URL_IN_NEW_TAB) {
                if (event.data.url && event.data.url !== "#") {
                    window.open(event.data.url, "_blank");
                }
            }
        },
    },
    mounted() {
        this.loadWebTab();
        document.addEventListener("keydown", this.handleKeydown);
        window.addEventListener("message", this.handleWidgetMessage);
    },
    beforeDestroy() {
        document.removeEventListener("keydown", this.handleKeydown);
        window.removeEventListener("message", this.handleWidgetMessage);
    },
    watch: {
        "$route.params.id"() {
            if (!this.webTabProp) {
                this.loadWebTab();
            }
        },
        webTabProp: {
            handler() {
                this.loadWebTab();
            },
            deep: true,
        },
        context: {
            handler() {
                if (this.contextType && this.context) {
                    this.loadWebTab();
                }
            },
            deep: true,
        },
    },
};
</script>

<style lang="scss" scoped>
.web-tab-viewer {
    width: 100%;
    height: 100vh;
    display: flex;
    flex-direction: column;
    background: linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%);

    // Loading State
    .viewer-loading {
        display: flex;
        justify-content: center;
        align-items: center;
        height: 100%;
        width: 100%;

        .loader-container {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 24px;

            .loader-spinner {
                width: 56px;
                height: 56px;
                border: 4px solid rgba(99, 102, 241, 0.2);
                border-top-color: #6366f1;
                border-radius: 50%;
                animation: spin 1s linear infinite;
            }

            .loader-text {
                font-size: 16px;
                font-weight: 500;
                color: #4b5563;
                letter-spacing: -0.3px;
            }
        }
    }

    // Error State
    .viewer-error {
        display: flex;
        justify-content: center;
        align-items: center;
        height: 100%;
        width: 100%;
        padding: 24px;

        .error-container {
            max-width: 480px;
            background: white;
            border-radius: 20px;
            padding: 48px;
            text-align: center;
            box-shadow: 0 20px 60px rgba(0, 0, 0, 0.08);

            .error-icon-wrapper {
                width: 80px;
                height: 80px;
                margin: 0 auto 24px;
                background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;

                .error-icon {
                    font-size: 40px;
                    color: #dc2626;
                }
            }

            .error-title {
                font-size: 24px;
                font-weight: 700;
                color: #1f2937;
                margin-bottom: 12px;
                letter-spacing: -0.5px;
            }

            .error-message {
                font-size: 15px;
                color: #6b7280;
                margin-bottom: 32px;
                line-height: 1.6;
            }

            .error-actions {
                display: flex;
                gap: 12px;
                justify-content: center;

                button {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 12px 24px;
                    border-radius: 12px;
                    font-size: 14px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.2s ease;

                    .material-icons {
                        font-size: 18px;
                    }
                }

                .btn-retry {
                    background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
                    color: white;
                    border: none;
                    box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);

                    &:hover {
                        transform: translateY(-2px);
                        box-shadow: 0 6px 20px rgba(99, 102, 241, 0.4);
                    }
                }

                .btn-back {
                    background: white;
                    color: #4b5563;
                    border: 1px solid #e5e7eb;

                    &:hover {
                        background: #f9fafb;
                        border-color: #d1d5db;
                    }
                }
            }
        }
    }

    // Content
    .viewer-content {
        display: flex;
        flex-direction: column;
        height: 100%;

        &.embedded {
            background: transparent;
            height: 100%;
        }

        .embedded-toolbar {
            display: flex;
            justify-content: flex-end;
            gap: 4px;
            padding: 4px 8px;
            background: #f3f4f6;
            border-bottom: 1px solid #e5e7eb;

            .btn-icon-small {
                width: 28px;
                height: 28px;
                border-radius: 6px;
                background: white;
                border: 1px solid #e5e7eb;
                display: flex;
                align-items: center;
                justify-content: center;
                cursor: pointer;
                transition: all 0.2s ease;
                color: #6b7280;

                .material-icons {
                    font-size: 16px;
                }

                &:hover {
                    background: #e5e7eb;
                    color: #1f2937;
                }
            }
        }

        .viewer-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 12px 20px;
            background: white;
            border-bottom: 1px solid #e5e7eb;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
            z-index: 10;

            .header-left {
                display: flex;
                align-items: center;
                gap: 16px;

                .header-info {
                    display: flex;
                    align-items: center;
                    gap: 12px;

                    .header-icon {
                        font-size: 28px;
                    }

                    .header-text {
                        display: flex;
                        flex-direction: column;

                        .header-title {
                            font-size: 18px;
                            font-weight: 700;
                            color: #1f2937;
                            margin: 0;
                            letter-spacing: -0.3px;
                        }

                        .header-desc {
                            font-size: 12px;
                            color: #6b7280;
                            margin-top: 2px;
                        }
                    }
                }
            }

            .header-right {
                display: flex;
                align-items: center;
                gap: 8px;
            }

            .btn-icon {
                width: 40px;
                height: 40px;
                border-radius: 10px;
                background: #f3f4f6;
                border: none;
                display: flex;
                align-items: center;
                justify-content: center;
                cursor: pointer;
                transition: all 0.2s ease;
                color: #4b5563;

                .material-icons {
                    font-size: 20px;
                }

                &:hover {
                    background: #e5e7eb;
                    color: #1f2937;
                }

                &.fullscreen-btn.active {
                    background: #6366f1;
                    color: white;
                }
            }
        }

        .iframe-container {
            flex: 1;
            position: relative;
            background: white;
            margin: 16px;
            border-radius: 12px;
            overflow: hidden;
            box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);

            &.fullscreen {
                position: fixed;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                margin: 0;
                border-radius: 0;
                z-index: 1000;
            }

            &.embedded {
                margin: 0;
                border-radius: 0;
                box-shadow: none;
            }

            .web-tab-iframe {
                width: 100%;
                height: 100%;
                border: none;
            }
        }
    }
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

// Dark mode support
.dark-mode {
    .web-tab-viewer {
        background: linear-gradient(135deg, #1f2937 0%, #111827 100%);

        .viewer-loading {
            .loader-container {
                .loader-text {
                    color: #9ca3af;
                }
            }
        }

        .viewer-error {
            .error-container {
                background: #1f2937;
                box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);

                .error-title {
                    color: #f9fafb;
                }

                .error-message {
                    color: #9ca3af;
                }

                .btn-back {
                    background: #374151;
                    color: #d1d5db;
                    border-color: #4b5563;

                    &:hover {
                        background: #4b5563;
                    }
                }
            }
        }

        .viewer-content {
            .viewer-header {
                background: #1f2937;
                border-bottom-color: #374151;

                .header-left {
                    .header-info {
                        .header-text {
                            .header-title {
                                color: #f9fafb;
                            }

                            .header-desc {
                                color: #9ca3af;
                            }
                        }
                    }
                }

                .btn-icon {
                    background: #374151;
                    color: #d1d5db;

                    &:hover {
                        background: #4b5563;
                        color: #f9fafb;
                    }
                }
            }

            .iframe-container {
                background: #1f2937;
                box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
            }
        }
    }
}

// Responsive
@media (max-width: 768px) {
    .web-tab-viewer {
        .viewer-content {
            .viewer-header {
                padding: 12px 16px;

                .header-left {
                    .header-info {
                        .header-text {
                            .header-title {
                                font-size: 16px;
                            }

                            .header-desc {
                                display: none;
                            }
                        }
                    }
                }
            }

            .iframe-container {
                margin: 8px;
                border-radius: 8px;
            }
        }

        .viewer-error {
            .error-container {
                padding: 32px 24px;

                .error-actions {
                    flex-direction: column;
                }
            }
        }
    }
}
</style>
